import {alias, createModelSchema, list} from "serializr";
import {BaseObject} from "@/common/BaseObject";
import {Schema} from "@/common/Schema";

import {TriggerUtils} from "../trigger/core/TriggerUtils";
import {TutorialKind} from "./TutorialKind";
import {CellConfig} from "../cells/CellConfig";
import {ItemKey} from "../item/core/ItemKey";
import {GuardConfig} from "@/modules/redactor/models/guard/GuardConfig";
import {MapGroupModel} from "@/modules/redactor/models/map/MapGroupModel";

export class TutorialModel extends BaseObject {

    constructor() {
        super();

        this._v2 = false;

        this._id = null;
        this._key = null;
        this._name = null;
        this._kind = TutorialKind.CUSTOM;
        this._master = false;
        this._showNext = null;

        this._item = null;
        this._strict = false;
        this._camera = false;
        this._lightMode = false;
        this._ignorePopup = false;
        this._overNotice = false;
        this._blockedByV1 = false;
        this._hidesDialogs = false;

        this._priority = 0;
        this._cells = [];

        this._autoClose = 0;
        this._delayOnShow = 0;

        this._guard = new GuardConfig();
        this._triggersAvailable = [];
        this._triggersComplete = [];

        this._shared = 0;

        this._mapGroup = null;
    }

    get v2() {
        return this._v2;
    }

    set v2(value) {
        this._v2 = value;

        if (!value) {
            this.blockedByV1 = false;
        }
    }

    get id() {
        return this._id;
    }

    set id(value) {
        this._id = value;
    }

    get mapGroup() {
        return this._mapGroup;
    }

    set mapGroup(value) {
        this._mapGroup = value;
    }

    get key() {
        return this._key;
    }

    set key(value) {
        this._key = value;
    }

    get name() {
        return this._name;
    }

    set name(value) {
        this._name = value;
    }

    get kind() {
        return this._kind;
    }

    set kind(value) {
        this._kind = value;
    }

    get master() {
        return this._master;
    }

    set master(value) {
        this._master = value;
    }

    get showNext() {
        return this._showNext;
    }

    set showNext(value) {
        this._showNext = value;
    }

    /******************************/
    get item() {
        return this._item;
    }

    set item(value) {
        this._item = value;
    }

    get priority() {
        return this._priority;
    }

    set priority(value) {
        this._priority = value;
    }

    get camera() {
        return this._camera;
    }

    set camera(value) {
        this._camera = value;
    }

    get strict() {
        return this._strict;
    }

    set strict(value) {
        this._strict = value;
    }

    get ignorePopup() {
        return this._ignorePopup;
    }

    set ignorePopup(value) {
        this._ignorePopup = value;
    }

    get overNotice() {
        return this._overNotice;
    }

    set overNotice(value) {
        this._overNotice = value;
    }

    get cells() {
        return this._cells;
    }

    set cells(value) {
        this._cells = value;
    }

    get autoClose() {
        return this._autoClose;
    }

    set autoClose(value) {
        this._autoClose = value;
    }

    get delayOnShow() {
        return this._delayOnShow;
    }

    set delayOnShow(value) {
        this._delayOnShow = value;
    }

    get guard() {
        return this._guard;
    }

    set guard(value) {
        this._guard = value;
    }

    get triggersAvailable() {
        return this._triggersAvailable;
    }

    set triggersAvailable(value) {
        this._triggersAvailable = value;
    }

    get triggersComplete() {
        return this._triggersComplete;
    }

    set triggersComplete(value) {
        this._triggersComplete = value;
    }

    get shared() {
        return this._shared;
    }

    set shared(value) {
        this._shared = value;
    }

    set blockedByV1(value) {
        this._blockedByV1 = value;
    }

    get blockedByV1() {
        return this._blockedByV1;
    }

    set hidesDialogs(value) {
        this._hidesDialogs = value;
    }

    get hidesDialogs() {
        return this._hidesDialogs;
    }

    set lightMode(value) {
        this._lightMode = value;
    }

    get lightMode() {
        return this._lightMode;
    }


    valid() {
        if (!this.id) {
            return false;
        }

        if (!TutorialKind.is(this.kind)) {
            return false;
        }

        if (this.item && (!this.item.valid() || this.item.any())) {
            return false;
        }

        if (this.cells.some(v => !v || !v.valid())) {
            return false;
        }

        if (!Number.isInteger(this.priority) || this.priority < 0) {
            return false;
        }

        if (Number.isNaN(this.autoClose) || this.autoClose < 0) {
            return false;
        }

        if (this.guard && !this.guard.valid()) {
            return false;
        }

        if (this.triggersAvailable.some(v => !v || !v.valid())) {
            return false;
        }

        if (this.triggersComplete.some(v => !v || !v.valid())) {
            return false;
        }

        if (this.mapGroup && (MapGroupModel.isGlobal(this.mapGroup) || MapGroupModel.isMain(this.mapGroup))) {
            return false;
        }

        return true;
    }

    copy() {
        return super.copy();
    }

    clone(target = undefined) {
        if (!target) {
            return super.clone(new TutorialModel());
        } else if (target instanceof TutorialModel) {
            return super.clone(target);
        } else {
            console.assert(false);
            return undefined;
        }
    }
}

createModelSchema(TutorialModel, {
    _v2: alias("v2", Schema.primitive()),
    _id: alias("id", Schema.primitive()),
    _key: alias("key", Schema.primitive()),
    _name: alias("name", Schema.primitive()),
    _kind: alias("kind", TutorialKind.schema()),
    _master: alias("master", Schema.primitive()),
    _showNext: alias("show_next", Schema.primitive()),

    _item: alias("item", ItemKey.schema()),
    _camera: alias("camera", Schema.primitive()),
    _strict: alias("strict", Schema.primitive()),
    _ignorePopup: alias("ignore_popup", Schema.primitive()),
    _overNotice: alias("over_notice", Schema.primitive()),
    _cells: alias("cells", list(CellConfig.schema())),
    _priority: alias("priority", Schema.primitive()),
    _autoClose: alias("auto_close", Schema.primitive()),
    _delayOnShow: alias("delay_show", Schema.primitive()),

    _guard: alias("guard", GuardConfig.schema()),
    _triggersAvailable: alias("triggers_available", TriggerUtils.schema()),
    _triggersComplete: alias("triggers_complete", TriggerUtils.schema()),
    
    _shared: alias("shared", Schema.primitive()),
    _blockedByV1: alias("blocked_by_v1", Schema.primitive()),
    _hidesDialogs: alias("hides_dialogs", Schema.primitive()),
    _lightMode: alias("light_mode", Schema.primitive()),
    _mapGroup: alias("map_group", MapGroupModel.schema()),
});